import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcPaymentsV1IncomingAchQueryServiceIncomingAchGet } from '@interxfi/api-client/generated/hooks/ixc-payments-v1-incoming-ach-query-service-incoming-ach-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcPaymentsV1IncomingAchState } from '@interxfi/types/generated/api';
import { PaymentsIncomingAchEntityMenu } from '../../../../../../../../../../features/payments/incoming-ach/payments-incoming-ach-entity-menu';
import { PaymentsIncomingAchEventSummary } from '../../../../../../../../../../features/payments/incoming-ach/payments-incoming-ach-event';
import { getPaymentsIncomingAchStateListItems } from '../../../../../../../../../../features/payments/incoming-ach/payments-incoming-ach-list-items';
import { getPaymentsIncomingAchDetailPath } from '../../../../../../../../../../features/payments/incoming-ach/payments-incoming-ach-routing';
import { PaymentsIncomingAchStatusPill } from '../../../../../../../../../../features/payments/incoming-ach/payments-incoming-ach-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/finance/_finance/payment/incoming-ach/_incoming-ach/$achId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `incoming-ach-payment-${params.achId}`,
        label: 'Incoming ACH',
        to: getPaymentsIncomingAchDetailPath(params.achId),
        suffix: <CopyButton content={params.achId} label="Copy ACH ID" tooltip={{ copy: params.achId, copied: 'Copied ACH ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Incoming ACH Payment'),
  component: PaymentsIncomingAchDetail,
});

function getListItems(data: IxcPaymentsV1IncomingAchState | undefined) {
  const items = getPaymentsIncomingAchStateListItems(data);

  return {
    general: {
      left: [
        {
          id: 'main',
          items: [items.data.spec.amount],
        },
      ] satisfies ReadonlyListGroup[],
      right: [
        items.metadata.createdAt,
        items.metadata.updatedAt,
        items.achId,
        items.data.spec.accountNumberId,
        items.data.spec.transferId,
        items.data.spec.accountId,
        items.data.spec.programId,
      ] satisfies ReadonlyListItem[],
    },
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load incoming ACH', 'view this incoming ACH');

function PaymentsIncomingAchDetail() {
  const { achId } = Route.useParams();
  const request = { achId } as const;
  const { data, error, isPending } = useIxcPaymentsV1IncomingAchQueryServiceIncomingAchGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.incomingAch), [data?.incomingAch]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <PaymentsIncomingAchStatusPill isPending={showPending} size="large" status={data?.incomingAch?.status} />
          </HorizontalStack>

          <PaymentsIncomingAchEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <PaymentsIncomingAchEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.general.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
